// Core API Response Types
export interface ApiResponse<T = any> {
  data: T;
  success: boolean;
  message?: string;
  errors?: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface ApiError {
  message: string;
  statusCode: number;
  errors?: Record<string, string[]>;
  deliveryRecordId?: string;
  messageId?: string;
  retryEndpoint?: string;
}

// Authentication Types
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

// User Types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: UserRole;
  isActive: boolean;
  companyId?: string;
  company?: Company;
  profileImageUrl?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string | null;
  department?: string;
  jobTitle?: string;
  phoneNumber?: string;
  timezone?: string;
  preferences?: any;
  lastSeenAt?: string;
  ticketCount?: number;
}

export enum UserRole {
  EndUser = 0,
  Agent = 1,
  Manager = 2,
  Admin = 3
}

// Utility functions for UserRole
export const getUserRoleString = (role: UserRole): string => {
  switch (role) {
    case UserRole.EndUser: return 'EndUser';
    case UserRole.Agent: return 'Agent';
    case UserRole.Manager: return 'Manager';
    case UserRole.Admin: return 'Admin';
    default: return 'Unknown';
  }
};

export const getUserRoleFromString = (roleString: string): UserRole => {
  switch (roleString.toLowerCase()) {
    case 'enduser': return UserRole.EndUser;
    case 'agent': return UserRole.Agent;
    case 'manager': return UserRole.Manager;
    case 'admin': return UserRole.Admin;
    // Handle alternative role strings
    case 'administrator': return UserRole.Admin;
    case 'user': return UserRole.EndUser;
    case 'customer': return UserRole.EndUser;
    default: 
      console.warn('Unknown role string received:', roleString);
      return UserRole.EndUser;
  }
};

export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  role: UserRole;
  companyId?: string;
  department?: string;
  jobTitle?: string;
  phone?: string;
  sendWelcomeEmail?: boolean;
  isActive?: boolean;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  department?: string;
  jobTitle?: string;
  timeZone?: string;
  language?: string;
  avatarUrl?: string;
  role?: UserRole;
  isActive?: boolean;
}

// Company Types
export interface Company {
  id: string;
  name: string;
  domain?: string;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string | null;
  settings?: any;
  customFields?: any;
}

// Ticket Types
export type TicketUser = Omit<User, 'createdAt'> & { createdAt: string | null };
export type TicketCompany = Omit<Company, 'createdAt'> & { createdAt: string | null };

export interface Ticket {
  id: string;
  ticketNumber?: string;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  source: TicketSource;
  requesterId: string;
  requester: TicketUser;
  assignedToId?: string;
  assignedTo?: TicketUser;
  companyId?: string;
  company?: TicketCompany;
  tags: string[];
  customFields: Record<string, any>;
  createdAt: string;
  updatedAt: string | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  firstResponseAt?: string | null;
  conversations: Conversation[];
  attachments: Attachment[];
}

export enum TicketStatus {
  New = 'New',
  Open = 'Open',
  InProgress = 'InProgress',
  Pending = 'Pending',
  Resolved = 'Resolved',
  Closed = 'Closed',
  OnHold = 'OnHold',
  Cancelled = 'Cancelled'
}

export enum TicketPriority {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Urgent = 'Urgent',
  Critical = 'Critical'
}

// Utility functions for TicketStatus and TicketPriority
export const getTicketStatusString = (status: TicketStatus | number | string): string => {
  // Handle numeric values from backend
  if (typeof status === 'number') {
    switch (status) {
      case 0: return 'New';
      case 1: return 'Open';
      case 2: return 'Pending';
      case 3: return 'Resolved';
      case 4: return 'Closed';
      case 5: return 'On Hold';
      case 6: return 'Cancelled';
      default: return 'Unknown';
    }
  }

  // Handle enum values
  if (typeof status === 'string') {
    switch (status) {
      case TicketStatus.InProgress: return 'In Progress';
      case TicketStatus.OnHold: return 'On Hold';
      default: return status;
    }
  }

  return String(status);
};

export const getTicketPriorityString = (priority: TicketPriority | number | string): string => {
  // Handle numeric values from backend
  if (typeof priority === 'number') {
    switch (priority) {
      case 0: return 'Low';
      case 1: return 'Medium';
      case 2: return 'High';
      case 3: return 'Urgent';
      case 4: return 'Critical';
      default: return 'Unknown';
    }
  }

  // Handle enum values or direct strings
  return String(priority);
};

export enum TicketSource {
  Email = 'Email',
  WebForm = 'Web',
  Phone = 'Phone',
  Chat = 'Chat',
  API = 'API',
  Social = 'Social'
}

export interface CreateTicketRequest {
  subject: string;
  description: string;
  priority?: TicketPriority;
  source?: TicketSource;
  requesterId?: string;
  assignedToId?: string;
  companyId?: string;
  tags?: string | string[];
  customFields?: Record<string, any>;
}

export interface UpdateTicketRequest {
  subject?: string;
  description?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  assignedToId?: string;
  tags?: string | string[];
  customFields?: Record<string, any>;
}

// Conversation Types
export interface Conversation {
  id: string;
  ticketId: string;
  userId: string;
  user?: User;
  author?: User; // Backend returns this field instead of user
  content: string;
  htmlContent?: string; // Processed HTML content
  isInternal: boolean;
  isPublic?: boolean; // Backend field (opposite of isInternal)
  type?: ConversationType; // Backend DTO field; normalised to conversationType at the UI boundary
  conversationType: ConversationType;
  createdAt: string;
  updatedAt: string;
  attachments: Attachment[];
  isFromEmail?: boolean;
  emailDirection?: EmailDirection;
  emailMessageId?: string;
  emailThreadId?: string;
  emailSubject?: string;
  fromEmail?: string;
  toEmails?: string;
  ccEmails?: string;
  emailDeliveries?: EmailDelivery[];
}

export enum EmailDirection {
  Inbound = 0,
  Outbound = 1,
}

export enum EmailDeliveryStatus {
  Queued = 0,
  Sent = 1,
  Failed = 2,
  Bounced = 3,
  Delivered = 4,
}

export interface EmailDelivery {
  id: string;
  direction: EmailDirection;
  status: EmailDeliveryStatus;
  errorMessage?: string;
  receivedAt: string;
  processedAt?: string;
  retryOfEmailMessageId?: string;
}

export enum ConversationType {
  Reply = 0,
  Note = 1,
  Activity = 2,
  StatusChange = 3,
  Assignment = 4,
  PriorityChange = 5,
  Escalation = 6
}

export interface ConversationThreadDto {
  ticketId: string;
  ticketNumber: string;
  ticketSubject: string;
  conversations: Conversation[];
  totalConversations: number;
  publicConversations: number;
  privateNotes: number;
  lastActivity?: string;
}

export interface CreateConversationRequest {
  ticketId: string;
  content: string;
  isInternal: boolean;
  conversationType?: ConversationType;
  statusChangeAfterSend?: TicketStatus;
  attachmentIds?: string[];
}

export interface UpdateConversationRequest {
  content?: string;
  isInternal?: boolean;
}

// Attachment Types
export interface Attachment {
  id: string;
  fileName: string;
  displayName?: string;
  originalFileName?: string;
  contentType: string;
  sizeBytes?: number;
  fileSize?: number;
  formattedSize?: string;
  filePath?: string;
  downloadUrl?: string;
  ticketId?: string;
  conversationId?: string;
  uploadedById: string;
  uploadedBy?: User;
  uploaderName?: string;
  isAvailable?: boolean;
  createdAt: string;
}

export interface UploadAttachmentRequest {
  file: File;
  ticketId?: string;
  conversationId?: string;
}

// Query Parameters
export interface TicketQueryParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  statuses?: TicketStatus[];
  priorities?: TicketPriority[];
  assignedAgentIds?: string[];
  requesterIds?: string[];
  companyIds?: string[];
  categories?: string[];
  sources?: TicketSource[];
  tags?: string[];
  createdAfter?: string;
  createdBefore?: string;
  updatedAfter?: string;
  updatedBefore?: string;
  dueBefore?: string;
  isEscalated?: boolean;
  slaBreached?: boolean;
  hasAttachments?: boolean;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onlyUnassigned?: boolean;
  onlyMyTickets?: boolean;
}

export interface UserQueryParams {
  page?: number;
  pageSize?: number;
  role?: UserRole[];
  isActive?: boolean;
  companyId?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Dashboard/Statistics Types
export interface DashboardStats {
  totalTickets: number;
  openTickets: number;
  resolvedTickets: number;
  averageResolutionTime: number;
  ticketsByStatus: Record<TicketStatus, number>;
  ticketsByPriority: Record<TicketPriority, number>;
  recentTickets: Ticket[];
}

export interface TicketMetrics {
  totalCount: number;
  openCount: number;
  inProgressCount: number;
  resolvedCount: number;
  closedCount: number;
  averageFirstResponseTime: number;
  averageResolutionTime: number;
  satisfactionScore?: number;
}

// Search Types
export interface SearchResult {
  tickets: Ticket[];
  users: User[];
  companies: Company[];
  totalResults: number;
}

export interface SearchParams {
  query: string;
  filters?: {
    types?: ('tickets' | 'users' | 'companies')[];
    dateRange?: {
      from: string;
      to: string;
    };
  };
}

// SLA Types
export interface SLAPolicy {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  isDefault: boolean;
  businessHoursId: string;
  businessHours?: BusinessHours;
  targets: SLATarget[];
  conditions: SLACondition[];
  createdAt: string;
  updatedAt: string;
}

export interface SLATarget {
  id: string;
  slaId: string;
  priority: TicketPriority;
  firstResponseTimeMinutes: number;
  resolutionTimeMinutes: number;
  escalationEnabled: boolean;
  escalationTimeMinutes?: number;
}

export interface SLACondition {
  id: string;
  slaId: string;
  field: string;
  operator: string;
  value: string;
}

export interface BusinessHours {
  id: string;
  name: string;
  timeZone: string;
  isDefault: boolean;
  schedules: BusinessHoursSchedule[];
  holidays: Holiday[];
  createdAt: string;
  updatedAt: string;
}

export interface BusinessHoursSchedule {
  id: string;
  businessHoursId: string;
  dayOfWeek: number; // 0 = Sunday, 1 = Monday, etc.
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
  isEnabled: boolean;
}

export interface Holiday {
  id: string;
  businessHoursId: string;
  name: string;
  date: string; // ISO date string
  isRecurring: boolean;
  recurringType?: 'yearly' | 'monthly';
}

export interface EscalationRule {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  slaId?: string;
  conditions: EscalationCondition[];
  actions: EscalationAction[];
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface EscalationCondition {
  id: string;
  escalationRuleId: string;
  field: string;
  operator: string;
  value: string;
}

export interface EscalationAction {
  id: string;
  escalationRuleId: string;
  actionType: EscalationActionType;
  parameters: Record<string, any>;
  order: number;
}

export enum EscalationActionType {
  EmailNotification = 'EmailNotification',
  ReassignTicket = 'ReassignTicket',
  EscalateToManager = 'EscalateToManager',
  SetPriority = 'SetPriority',
  AddTag = 'AddTag',
  WebhookCall = 'WebhookCall'
}

export interface SLATracking {
  id: string;
  ticketId: string;
  slaId: string;
  sla: SLAPolicy;
  firstResponseDueAt?: string;
  resolutionDueAt?: string;
  firstResponseAt?: string;
  resolvedAt?: string;
  firstResponseBreached: boolean;
  resolutionBreached: boolean;
  escalationLevel: number;
  status: SLAStatus;
  createdAt: string;
  updatedAt: string;
}

export enum SLAStatus {
  Active = 'Active',
  Paused = 'Paused',
  Completed = 'Completed',
  Breached = 'Breached'
}

export interface SLAMetrics {
  totalTickets: number;
  complianceRate: number;
  firstResponseComplianceRate: number;
  resolutionComplianceRate: number;
  averageFirstResponseTime: number;
  averageResolutionTime: number;
  breachedTickets: number;
  escalatedTickets: number;
  currentlyAtRisk: number;
}

export interface SLAPerformanceData {
  period: string;
  complianceRate: number;
  firstResponseRate: number;
  resolutionRate: number;
  averageFirstResponse: number;
  averageResolution: number;
  totalTickets: number;
  breachedTickets: number;
}

// Request/Response types for SLA operations
export interface CreateSLAPolicyRequest {
  name: string;
  description?: string;
  isActive: boolean;
  isDefault: boolean;
  businessHoursId: string;
  targets: CreateSLATargetRequest[];
  conditions: CreateSLAConditionRequest[];
}

export interface CreateSLATargetRequest {
  priority: TicketPriority;
  firstResponseTimeMinutes: number;
  resolutionTimeMinutes: number;
  escalationEnabled: boolean;
  escalationTimeMinutes?: number;
}

export interface CreateSLAConditionRequest {
  field: string;
  operator: string;
  value: string;
}

export interface CreateBusinessHoursRequest {
  name: string;
  timeZone: string;
  isDefault: boolean;
  schedules: CreateBusinessHoursScheduleRequest[];
  holidays: CreateHolidayRequest[];
}

export interface CreateBusinessHoursScheduleRequest {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isEnabled: boolean;
}

export interface CreateHolidayRequest {
  name: string;
  date: string;
  isRecurring: boolean;
  recurringType?: 'yearly' | 'monthly';
}

export interface CreateEscalationRuleRequest {
  name: string;
  description?: string;
  isActive: boolean;
  slaId?: string;
  conditions: CreateEscalationConditionRequest[];
  actions: CreateEscalationActionRequest[];
  order: number;
}

export interface CreateEscalationConditionRequest {
  field: string;
  operator: string;
  value: string;
}

export interface CreateEscalationActionRequest {
  actionType: EscalationActionType;
  parameters: Record<string, any>;
  order: number;
}
